import type {ReactNode, RefObject} from 'react'
import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {IconButton, useFocusTrap, useOnEscapePress} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import styles from './panel.module.css'
import {useViewportFitHeight} from './use-viewport-fit-height'
import {useDialogFillHeight} from './use-dialog-fill-height'

interface PanelHeaderProps {
  children: ReactNode
}

function PanelHeader({children}: PanelHeaderProps) {
  return <>{children}</>
}

interface PanelBodyProps {
  children: ReactNode
  className?: string
}

function PanelBody({children, className}: PanelBodyProps) {
  return <div className={clsx('flex-1 overflow-auto', className ?? 'tmp-p-3')}>{children}</div>
}

interface PanelFooterProps {
  children: ReactNode
}

function PanelFooter({children}: PanelFooterProps) {
  return <>{children}</>
}

interface PanelViewportFillProps {
  children: ReactNode
  fixedHeight?: boolean
  bottomInsetPx?: number
}

/**
 * Opt-in wrapper that constrains its children to the visible viewport area.
 *
 * Use inside `Panel.Body` when the panel content has its own internal scroll
 * management (e.g. a chat with a sticky input at the bottom) and must not
 * extend below the viewport -- even before the sticky sidebar fully takes effect.
 *
 * When `fixedHeight` is true the height is measured on mount and window resize
 * only, preventing content from shifting as the global header scrolls in/out.
 */
function PanelViewportFill({children, fixedHeight, bottomInsetPx}: PanelViewportFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useViewportFitHeight(ref, {
    recalculateOnScroll: !fixedHeight,
    bottomInsetPx,
  })
  return (
    <div ref={ref} className={styles.ViewportFill}>
      {children}
    </div>
  )
}

interface PanelDialogFillProps {
  children: ReactNode
}

/**
 * Wrapper for Dialog body content that fills the available height.
 *
 * Primer's Dialog body gets its height from flex layout, which breaks
 * CSS percentage-height resolution for children.  This component
 * uses a ResizeObserver on the parent to set an explicit pixel height.
 */
function PanelDialogFill({children}: PanelDialogFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDialogFillHeight(ref)
  return (
    <div ref={ref} className={styles.DialogFill}>
      {children}
    </div>
  )
}

interface PanelProps {
  /**
   * Heading content rendered on the left of the panel header. Pass a string for
   * a plain title, or a node (e.g. a panel switcher) for richer headers.
   */
  heading: ReactNode
  /** Optional id supplied by rich heading content rendered through a portal. */
  headingId?: string
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6
  children: ReactNode
  headerActions?: ReactNode
  closeButtonRef: RefObject<HTMLButtonElement | null>
  returnFocusRef: RefObject<HTMLElement | null>
  onClose: () => void
  className?: string
  headerClassName?: string
}

/**
 * Self-contained, presentational compound component for docked side panel content.
 *
 * Renders the panel heading, a close button with managed focus refs, and
 * `Panel.Header`, `Panel.Body`, and `Panel.Footer` slot sub-components laid out
 * in a flex column. The body region scrolls while header and footer stay fixed.
 *
 * State (focus refs and close handler) is passed in via props so host pages can
 * drive it from whatever context they own. Can be rendered directly inside a
 * sidebar or portaled into a docked panel content slot via `createPortal`.
 */
function Panel({
  heading,
  headingId: providedHeadingId,
  headingLevel = 3,
  children,
  headerActions,
  closeButtonRef,
  returnFocusRef,
  onClose,
  className,
  headerClassName,
}: PanelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const generatedHeadingId = useId()
  const headingId = providedHeadingId ?? generatedHeadingId
  const HeadingTag = `h${headingLevel}` as const
  const [isFullscreen, setIsFullscreen] = useState(
    () => ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')?.matches ?? false,
  )

  const handleClose = useCallback(() => {
    const returnTarget = returnFocusRef.current
    onClose()
    // Return focus to the element that opened the panel
    if (returnTarget) {
      requestAnimationFrame(() => returnTarget.focus())
    }
  }, [onClose, returnFocusRef])

  const [slots] = useSlots(children, {
    header: PanelHeader,
    body: PanelBody,
    footer: PanelFooter,
  })

  useFocusTrap({containerRef, disabled: !isFullscreen})

  // Only close on Escape if focus is within the panel
  useOnEscapePress(() => {
    if (containerRef.current?.contains(document.activeElement)) {
      handleClose()
    }
  }, [handleClose])

  // When SplitPageLayout.Sidebar transitions to fullscreen,
  // move focus to the close button so focus isn't behind the panel.
  useEffect(() => {
    const mediaQuery = ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')

    const handleChange = (event: MediaQueryListEvent) => {
      setIsFullscreen(event.matches)
      if (event.matches && !containerRef.current?.contains(document.activeElement)) {
        closeButtonRef.current?.focus()
      }
    }

    mediaQuery?.addEventListener('change', handleChange)
    return () => mediaQuery?.removeEventListener('change', handleChange)
  }, [closeButtonRef])

  return (
    <div
      ref={containerRef}
      role={isFullscreen ? 'dialog' : 'region'}
      aria-modal={isFullscreen ? true : undefined}
      aria-labelledby={headingId}
      className={clsx('d-flex flex-column height-full', className)}
    >
      <div className={clsx(styles.Header, headerClassName)}>
        <div className={styles.HeaderContent}>
          <HeadingTag id={headingId} className={styles.Heading}>
            {heading}
          </HeadingTag>
          <div className={styles.HeaderActions}>
            {headerActions}
            <IconButton
              ref={closeButtonRef}
              aria-label="Close side panel"
              icon={XIcon}
              variant="invisible"
              size="medium"
              onClick={handleClose}
            />
          </div>
        </div>
        {slots.header}
      </div>
      {slots.body}
      {slots.footer && <div className="tmp-p-3">{slots.footer}</div>}
    </div>
  )
}

Panel.Header = PanelHeader
Panel.Body = PanelBody
Panel.Footer = PanelFooter
Panel.ViewportFill = PanelViewportFill
Panel.DialogFill = PanelDialogFill

export {Panel}
